'use client';

/**
 * QR-based device pairing via NaCl sealed-box.
 *
 * Flow (Device B = new, Device A = existing owner of the account key):
 *   1. B generates an ephemeral Curve25519 keypair (eB_priv, eB_pub) and
 *      a random 128-bit pair room id. Shows a QR encoding
 *      `knitlib-pair/v1/<roomId>/<eB_pub_b64>`. Opens a WS to the relay
 *      room and waits for a `pair` envelope.
 *   2. A scans the QR, opens the same WS room, runs `nacl.box.seal` to
 *      encrypt the account key to `eB_pub`, sends it as a `pair` envelope.
 *      A closes the socket immediately.
 *   3. B receives the ciphertext, runs `nacl.box.seal.open` with eB_priv,
 *      extracts the account key, persists it, closes the socket, and joins
 *      the main sync room.
 *
 * Ciphertext is `nacl.box.seal(plaintext, eB_pub)` — a NaCl construction
 * that internally generates its own ephemeral sender keypair per message.
 * The relay sees only opaque bytes in a short-lived room. Adversaries who
 * photograph the QR but don't have eB_priv cannot decrypt; adversaries who
 * own the relay cannot forge a valid seal without the account key.
 *
 * The pair room id is non-secret (routing only). The ephemeral pubkey is
 * non-secret (the whole point of sealed-box). Security rests entirely on
 * eB_priv never leaving the device that generated it.
 */

import nacl from 'tweetnacl';

export const PAIR_QR_PREFIX = 'knitlib-pair/v1/';

export interface PairSession {
  roomId: string;
  ephemeralPublicKey: Uint8Array;
  ephemeralSecretKey: Uint8Array;
}

export function createPairSession(): PairSession {
  const keyPair = nacl.box.keyPair();
  // 128-bit random room id, hex-encoded, 32 chars.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const roomId = 'pair-' + Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  return {
    roomId,
    ephemeralPublicKey: keyPair.publicKey,
    ephemeralSecretKey: keyPair.secretKey,
  };
}

export function encodePairQR(session: PairSession): string {
  return `${PAIR_QR_PREFIX}${session.roomId}/${b64urlEncode(session.ephemeralPublicKey)}`;
}

export function decodePairQR(payload: string): { roomId: string; ephemeralPublicKey: Uint8Array } {
  if (!payload.startsWith(PAIR_QR_PREFIX)) {
    throw new Error('not a knitlib pairing QR');
  }
  const rest = payload.slice(PAIR_QR_PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash < 0) throw new Error('malformed pairing QR');
  const roomId = rest.slice(0, slash);
  const ephemeralPublicKey = b64urlDecode(rest.slice(slash + 1));
  if (!roomId.startsWith('pair-') || ephemeralPublicKey.length !== 32) {
    throw new Error('malformed pairing QR');
  }
  return { roomId, ephemeralPublicKey };
}

/**
 * Seal an account key to the new device's ephemeral pubkey. Only the
 * holder of the matching secret key can open it.
 */
export function sealAccountKey(accountKey: Uint8Array, ephemeralPublicKey: Uint8Array): Uint8Array {
  // nacl.box uses Curve25519 + XSalsa20 + Poly1305. We use the anonymous
  // sealed-box variant (from NaCl "crypto_box_seal") implemented manually
  // since tweetnacl doesn't ship it. Construction: a fresh ephemeral sender
  // keypair, a nonce derived from both pubkeys, then nacl.box.
  const ephSender = nacl.box.keyPair();
  const nonce = deriveSealNonce(ephSender.publicKey, ephemeralPublicKey);
  const ciphertext = nacl.box(accountKey, nonce, ephemeralPublicKey, ephSender.secretKey);
  // Payload: sender_pub (32) || ciphertext
  const out = new Uint8Array(32 + ciphertext.length);
  out.set(ephSender.publicKey, 0);
  out.set(ciphertext, 32);
  return out;
}

export function openSealedAccountKey(
  blob: Uint8Array,
  ephemeralPublicKey: Uint8Array,
  ephemeralSecretKey: Uint8Array,
): Uint8Array {
  if (blob.length < 32 + 16) throw new Error('sealed blob too short');
  const senderPub = blob.subarray(0, 32);
  const ciphertext = blob.subarray(32);
  const nonce = deriveSealNonce(senderPub, ephemeralPublicKey);
  const opened = nacl.box.open(ciphertext, nonce, senderPub, ephemeralSecretKey);
  if (!opened) throw new Error('sealed-box open failed — wrong key or tampered payload');
  if (opened.length !== 32) throw new Error('unexpected account key length');
  return opened;
}

/**
 * Standard libsodium-compatible sealed-box nonce derivation:
 *   nonce = BLAKE2b(sender_pub || recipient_pub, 24 bytes)
 * tweetnacl doesn't ship BLAKE2b, so we substitute SHA-256 (truncated to 24
 * bytes). This is non-standard for sealed-box but safe for our use because
 * the (pub, pub) pair is unique per pairing session and the nonce only needs
 * to be unique relative to this key pair — we never reuse ephemeral keys.
 */
function deriveSealNonce(senderPub: Uint8Array, recipientPub: Uint8Array): Uint8Array {
  const material = new Uint8Array(senderPub.length + recipientPub.length);
  material.set(senderPub, 0);
  material.set(recipientPub, senderPub.length);
  // Synchronous SHA-256 via stablelib (already in deps for the sync layer).
  // Lazy-import pattern keeps this file tree-shake friendly.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { hash: sha256 } = require('@stablelib/sha256') as { hash: (b: Uint8Array) => Uint8Array };
  return sha256(material).subarray(0, 24);
}

// ---------- base64url helpers (URL-safe for QR payloads) ----------

function b64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlDecode(str: string): Uint8Array {
  const pad = str.length % 4 === 0 ? '' : '='.repeat(4 - (str.length % 4));
  const b64 = str.replace(/-/g, '+').replace(/_/g, '/') + pad;
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

export function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
